import { memo } from 'react';

import { FiUsers, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { UseQueryResult } from 'react-query';
import { AxiosError } from 'axios';

import { FullSpeakersResult } from '@common/types/api';
import { Avatar } from '@components';

import * as S from './styles';

type SpeakersSectionProps = {
  speakersQuery: UseQueryResult<FullSpeakersResult, AxiosError>;
};

const SpeakersSection = ({ speakersQuery }: SpeakersSectionProps) => {
  const history = useHistory();
  const { search, ...location } = useLocation();
  const { t } = useTranslation();

  const queryParams = new URLSearchParams(search);

  const handleSpeakerClick = (speakerId: number) => {
    queryParams.set('speaker', `${speakerId}`);
    history.push({
      ...location,
      search: `?${queryParams.toString()}`,
    });
  };

  if (!speakersQuery.isSuccess) {
    return (
      <div style={{ marginBottom: '2.95rem' }}>
        <S.Header>
          <S.HeaderLeft>
            <FiUsers size={20} />
            <h2>{t('pages.lobby.speakers')}</h2>
          </S.HeaderLeft>
        </S.Header>
        <Skeleton height={200} />
      </div>
    );
  }

  const { speakers } = speakersQuery.data;
  if (!speakers.length) return null;

  const isCompactLayout = speakers.length > 4;
  const featured = speakers[0];
  const allFeatured = speakers.slice(0, 4);
  const others = speakers.slice(1, 7);
  const hasMore = speakers.length > 7;

  return (
    <div style={{ marginBottom: '2.95rem' }}>
      <S.Header>
        <S.HeaderLeft>
          <FiUsers size={20} />
          <h2>{t('pages.lobby.speakers')}</h2>
          {/* <S.CountBadge>
            {speakers.length}{' '}
            {t('pages.lobby.speakersConfirmed') || 'confirmados'}
          </S.CountBadge> */}
        </S.HeaderLeft>
        {hasMore && (
          <S.ShowAllLink to="/speakers">
            {t('pages.lobby.showAllSpeakers') || 'Ver todos'}
          </S.ShowAllLink>
        )}
      </S.Header>

      {isCompactLayout ? (
        <S.SpeakersLayout>
          <S.FeaturedCard onClick={() => handleSpeakerClick(featured.id)}>
            <S.FeaturedBg />
            <S.AvatarWrapper>
              <Avatar src={featured.photo} alt={featured.name} />
            </S.AvatarWrapper>
            <S.FeaturedName>{featured.name}</S.FeaturedName>
            <S.FeaturedRole>{featured.title}</S.FeaturedRole>
          </S.FeaturedCard>

          <S.OthersGridWrapper>
            <S.OthersGrid>
              {others.map((speaker) => (
                <S.CompactCard
                  key={`speaker-${speaker.id}`}
                  onClick={() => handleSpeakerClick(speaker.id)}
                >
                  <S.CompactAvatar>
                    <Avatar src={speaker.photo} alt={speaker.name} />
                  </S.CompactAvatar>
                  <S.CompactInfo>
                    <S.CompactNameRow>
                      <span>{speaker.name}</span>
                    </S.CompactNameRow>
                    <S.CompactRole>{speaker.title}</S.CompactRole>
                  </S.CompactInfo>
                  <FiChevronRight
                    size={16}
                    color="var(--colors-foreground-40)"
                  />
                </S.CompactCard>
              ))}
            </S.OthersGrid>
            {hasMore && (
              <S.OthersGridFade>
                <S.ShowMoreButton to="/speakers">
                  <FiChevronDown size={20} />
                </S.ShowMoreButton>
              </S.OthersGridFade>
            )}
          </S.OthersGridWrapper>
        </S.SpeakersLayout>
      ) : (
        <S.FeaturedGrid>
          {allFeatured.map((speaker) => (
            <S.GridCard
              key={`speaker-${speaker.id}`}
              onClick={() => handleSpeakerClick(speaker.id)}
            >
              <S.GridCardPhoto>
                <img src={speaker.photo} alt={speaker.name} />
              </S.GridCardPhoto>
              <S.GridCardInfo>
                <S.GridCardName>{speaker.name}</S.GridCardName>
                <S.GridCardRole>{speaker.title}</S.GridCardRole>
                {speaker.company_logo && (
                  <S.CompanyLogo>
                    <img src={speaker.company_logo} alt="Company" />
                  </S.CompanyLogo>
                )}
              </S.GridCardInfo>
            </S.GridCard>
          ))}
        </S.FeaturedGrid>
      )}
    </div>
  );
};

export default memo(SpeakersSection);
